<template>
  <div class="popup-content">
    <header v-if="hasHeader" class="popup-header">
      <slot name="header" />
    </header>

    <section class="popup-body" :class="bodyClass">
      <slot />
    </section>

    <footer v-if="hasFooter" class="popup-footer">
      <slot name="footer" />
    </footer>
  </div>
</template>

<script setup lang="ts">
import { Comment, computed, useSlots, type VNode } from 'vue';

type ClassValue = string | Record<string, boolean> | Array<string | Record<string, boolean>>;

interface Props {
  readonly bodyClass?: ClassValue;
}

defineProps<Props>();

const slots = useSlots();

function hasRenderableNodes(nodes: VNode[]): boolean {
  return nodes.some((n) => n.type !== Comment);
}

function hasSlot(name: 'header' | 'footer'): boolean {
  const fn = slots[name];
  if (!fn) return false;
  return hasRenderableNodes(fn());
}

const hasHeader = computed<boolean>(() => hasSlot('header'));
const hasFooter = computed<boolean>(() => hasSlot('footer'));
</script>

<style scoped>
.popup-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.popup-header {
  flex: 0 0 auto;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  box-sizing: border-box;
}

.popup-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 18px 20px;
  box-sizing: border-box;
}

.popup-footer {
  flex: 0 0 auto;
  padding: 14px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .popup-header {
    padding-top: calc(18px + env(safe-area-inset-top));
  }

  .popup-body {
    padding-bottom: calc(18px + env(safe-area-inset-bottom));
  }

  .popup-footer {
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
  }
}
</style>


